import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { logger } from './logger';
import { analyticsService } from './analytics/analyticsService';

interface EventParams {
  [key: string]: string | number | boolean | undefined;
}

export const ANALYTICS_EVENTS = {
  USER: {
    LANGUAGE_CHANGE: 'language_change',
    TRANSLATION: 'auto_translation'
  },
  NAVIGATION: {
    PAGE_VIEW: 'page_view',
    MENU_CLICK: 'menu_click',
    EXTERNAL_LINK: 'external_link_click',
    VIEW_CHANGE: 'view_change'
  },
  CONTENT: {
    DETAIL_CARD_OPEN: 'detail_card_open',
    DETAIL_CARD_NAVIGATION: 'detail_card_navigation',
    PLACE_CARD_OPEN: 'place_card_open',
    PLACE_CARD_CLOSE: 'place_card_close',
    INFO_WINDOW_OPEN: 'info_window_open',
    FILTER_CATEGORY: 'filter_category',
    FILTER_TYPE: 'filter_type',
    FILTER_DATE: 'filter_date'
  },
  INTERACTION: {
    BUTTON_CLICK: 'button_click',
    LINK_CLICK: 'link_click',
    ACTION_BUTTON: 'action_button_click'
  }
} as const;

class Analytics {
  private static instance: Analytics;
  private debug: boolean;

  private constructor() {
    this.debug = import.meta.env.VITE_FORCE_LOGS === 'true' || import.meta.env.MODE === 'development';
  }

  public static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  public async init(): Promise<void> {
    logger.debug('Analytics initialized', {
      category: 'Analytics',
      data: {
        timestamp: new Date().toISOString()
      }
    });
  }

  public pageView(path: string): void {
    analyticsService.trackEvent({
      event_type: 'navigation',
      event_name: 'page_view',
      event_data: {
        path,
        page_location: window.location.href,
        page_title: document.title,
        timestamp: new Date().toISOString()
      }
    });

    logger.debug('Page view tracked', {
      category: 'Analytics',
      data: {
        path,
        location: window.location.href,
        title: document.title,
        timestamp: new Date().toISOString()
      }
    });
  }

  public event(category: string, action: string, label?: string, params?: EventParams): void {
    analyticsService.trackEvent({
      event_type: category,
      event_name: action,
      event_data: {
        event_label: label,
        ...params,
        timestamp: new Date().toISOString()
      }
    });

    logger.debug('Event tracked', {
      category: 'Analytics',
      data: {
        event: action,
        category,
        label,
        params,
        timestamp: new Date().toISOString()
      }
    });
  }

  public trackDetailCard(isOpening: boolean, section: string, item: any, duration?: number): void {
    analyticsService.trackEvent({
      event_type: 'Content',
      event_name: isOpening ? ANALYTICS_EVENTS.CONTENT.DETAIL_CARD_OPEN : 'detail_card_close',
      event_data: {
        section_id: section,
        category: item.category,
        type: item.type,
        item_name: item.name,
        ...(duration ? { duration: Math.round(duration) } : {}),
        timestamp: new Date().toISOString()
      }
    });
  }

  public trackDetailCardNavigation(section: string, direction: 'prev' | 'next', fromItem: any, toItem: any): void {
    analyticsService.trackEvent({
      event_type: 'Content',
      event_name: ANALYTICS_EVENTS.CONTENT.DETAIL_CARD_NAVIGATION,
      event_data: {
        section_id: section,
        direction,
        from_item: fromItem.name,
        to_item: toItem.name,
        category: toItem.category,
        type: toItem.type,
        timestamp: new Date().toISOString()
      }
    });
  }

  public trackPlaceCard(isOpening: boolean, section: string, place: any): void {
    analyticsService.trackEvent({
      event_type: 'Content',
      event_name: isOpening ? ANALYTICS_EVENTS.CONTENT.PLACE_CARD_OPEN : ANALYTICS_EVENTS.CONTENT.PLACE_CARD_CLOSE,
      event_data: {
        section_id: section,
        place_name: place.name,
        items_count: place.items?.length || place.shows?.length,
        timestamp: new Date().toISOString()
      }
    });
  }

  public trackInfoWindow(place: any, section: string): void {
    analyticsService.trackEvent({
      event_type: 'Content',
      event_name: ANALYTICS_EVENTS.CONTENT.INFO_WINDOW_OPEN,
      event_data: {
        section_id: section,
        place_name: place.name,
        items_count: place.items?.length || place.shows?.length,
        timestamp: new Date().toISOString()
      }
    });
  }

  public trackFilter(section: string, type: 'category' | 'type' | 'date', value: string): void {
    const eventMap = {
      category: ANALYTICS_EVENTS.CONTENT.FILTER_CATEGORY,
      type: ANALYTICS_EVENTS.CONTENT.FILTER_TYPE,
      date: ANALYTICS_EVENTS.CONTENT.FILTER_DATE
    };

    analyticsService.trackEvent({
      event_type: 'Content',
      event_name: eventMap[type],
      event_data: {
        section_id: section,
        filter_type: type,
        filter_value: value,
        timestamp: new Date().toISOString()
      }
    });
  }

  public trackViewChange(section: string, view: 'map' | 'list'): void {
    analyticsService.trackEvent({
      event_type: 'Navigation',
      event_name: ANALYTICS_EVENTS.NAVIGATION.VIEW_CHANGE,
      event_data: {
        section_id: section,
        view_type: view,
        timestamp: new Date().toISOString()
      }
    });
  }

  public trackActionButton(section: string, action: string, item: any): void {
    analyticsService.trackEvent({
      event_type: 'Interaction',
      event_name: ANALYTICS_EVENTS.INTERACTION.ACTION_BUTTON,
      event_data: {
        section_id: section,
        action,
        item_name: item.name,
        category: item.category,
        type: item.type,
        timestamp: new Date().toISOString()
      }
    });
  }

  public trackExternalLink(url: string, text: string, section: string): void {
    analyticsService.trackEvent({
      event_type: 'Navigation',
      event_name: ANALYTICS_EVENTS.NAVIGATION.EXTERNAL_LINK,
      event_data: {
        section_id: section,
        url,
        text,
        location: window.location.pathname,
        timestamp: new Date().toISOString()
      }
    });
  }

  public trackLanguageChange(fromLang: string, toLang: string): void {
    analyticsService.trackEvent({
      event_type: 'User',
      event_name: ANALYTICS_EVENTS.USER.LANGUAGE_CHANGE,
      event_data: {
        from_language: fromLang,
        to_language: toLang,
        timestamp: new Date().toISOString()
      }
    });
  }

  public trackAutoTranslation(text: string, fromLang: string, toLang: string, success: boolean): void {
    analyticsService.trackEvent({
      event_type: 'User',
      event_name: ANALYTICS_EVENTS.USER.TRANSLATION,
      event_data: {
        from_language: fromLang,
        to_language: toLang,
        success,
        text_length: text ? text.length : 0,
        timestamp: new Date().toISOString()
      }
    });
  }
}

export const analytics = Analytics.getInstance();

// Hook personnalisé pour le tracking
export function useAnalytics() {
  const trackPageView = useCallback((path: string) => {
    analytics.pageView(path);
  }, []);

  const trackEvent = useCallback((
    category: string,
    action: string,
    label?: string,
    params?: EventParams
  ) => {
    analytics.event(category, action, label, params);
  }, []);

  return {
    trackPageView,
    trackEvent,
    analytics,
    events: ANALYTICS_EVENTS
  };
}

// Composant pour le tracking automatique des pages
export function AnalyticsRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    analytics.pageView(location.pathname);
  }, [location.pathname]);

  return null;
}